import { useState, useEffect, useRef } from 'react';
import { readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Server-side commissions query. Fetches a single page of the /commissions
 * list with filtering/sorting/pagination done on the backend, so the browser
 * never downloads the whole (~5.7k row) dataset.
 *
 * Returns { rows, totalCount, totalPayable, loading }.
 */
export const useCommissionsQuery = ({
  view = 'all',
  page = 1,
  pageSize = 100,
  search = '',
  manager = '',
  jobType = '',
  sortBy = '',
  sortDir = 'asc',
  reloadKey,
}) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);
  const [loading, setLoading] = useState(true);

  // Debounce only the free-text search so typing doesn't fire a request per
  // keystroke. Everything else applies immediately.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const reqId = useRef(0);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    const params = new URLSearchParams({
      paginated: 'true',
      view,
      page: String(page),
      page_size: String(pageSize),
      sort_dir: sortDir,
    });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (manager) params.set('manager', manager);
    if (jobType) params.set('job_type', jobType);
    if (sortBy) params.set('sort_by', sortBy);

    fetch(`${API_URL}/commissions?${params.toString()}`)
      .then(readResponseJSON)
      .then(data => {
        if (id !== reqId.current) return; // a newer request superseded this one
        setRows(data.rows || []);
        setTotalCount(data.total_count || 0);
        setTotalPayable(data.total_commission_payable || 0);
      })
      .catch(() => {
        if (id !== reqId.current) return;
        setRows([]);
        setTotalCount(0);
        setTotalPayable(0);
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  }, [view, page, pageSize, debouncedSearch, manager, jobType, sortBy, sortDir, reloadKey]);

  return { rows, totalCount, totalPayable, loading };
};
